import { useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Shield, Sparkles, UserPlus, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function GuildPicker() {
  const { createGuild, joinGuild, logout, user } = useAuth();
  const [mode, setMode] = useState("create"); // 'create' | 'join'
  const [name, setName] = useState("");
  const [invite, setInvite] = useState("");
  const [busy, setBusy] = useState(false);

  const handleCreate = async () => {
    if (name.trim().length < 3) { toast.error("Guild name must be 3+ characters"); return; }
    setBusy(true);
    try {
      const g = await createGuild(name.trim());
      toast.success(`Created ${g.name}!`);
      // Trigger reload so all data fetches re-run with new active guild
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create guild");
    }
    setBusy(false);
  };

  const handleJoin = async () => {
    if (!invite.trim()) { toast.error("Paste an invite code"); return; }
    setBusy(true);
    try {
      const g = await joinGuild(invite.trim());
      toast.success(`Joined ${g.name}!`);
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid invite");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="guild-picker">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-3 mb-6">
          <Sparkles className="h-8 w-8 text-purple-400" />
          <div>
            <h1 className="heading text-2xl font-bold tracking-tight uppercase">Welcome, {user?.name}</h1>
            <p className="text-sm text-muted-foreground">Pick a guild to get started.</p>
          </div>
        </div>

        <div className="bg-card/40 border border-border/40 rounded-sm p-1 mb-6 flex gap-1">
          <button data-testid="guild-mode-create" onClick={() => setMode("create")}
            className={`flex-1 px-3 py-2 text-xs font-mono uppercase tracking-wider rounded-sm transition-colors ${mode === "create" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
            <Shield className="h-3 w-3 inline mr-1" /> Create new
          </button>
          <button data-testid="guild-mode-join" onClick={() => setMode("join")}
            className={`flex-1 px-3 py-2 text-xs font-mono uppercase tracking-wider rounded-sm transition-colors ${mode === "join" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
            <UserPlus className="h-3 w-3 inline mr-1" /> Join existing
          </button>
        </div>

        {mode === "create" && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              You'll be the admin of this guild. All raids and players you import will be private to your guild only.
            </p>
            <Input
              data-testid="guild-create-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. No More Excuses, Vibe Check, Lock & Load…"
              className="bg-card/50 font-mono"
              maxLength={60}
            />
            <Button data-testid="guild-create-btn" onClick={handleCreate} disabled={busy}
              className="rounded-sm font-bold uppercase tracking-wider w-full bg-purple-500 hover:bg-purple-600 text-white gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />} Create guild
            </Button>
          </div>
        )}

        {mode === "join" && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Paste the invite code your guild admin sent you.
            </p>
            <Input
              data-testid="guild-join-code"
              value={invite}
              onChange={(e) => setInvite(e.target.value)}
              placeholder="invite code…"
              className="bg-card/50 font-mono"
            />
            <Button data-testid="guild-join-btn" onClick={handleJoin} disabled={busy}
              className="rounded-sm font-bold uppercase tracking-wider w-full bg-purple-500 hover:bg-purple-600 text-white gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Join guild
            </Button>
          </div>
        )}

        <button data-testid="guild-picker-logout" onClick={logout}
          className="mt-8 w-full text-xs text-muted-foreground hover:text-foreground flex items-center justify-center gap-2 transition-colors">
          <LogOut className="h-3 w-3" /> Log out
        </button>
      </div>
    </div>
  );
}
